import { defineStore } from 'pinia'
import { nextTick } from 'vue'
import api from '~/api'
import { APIError } from '~/api/APIErrors'
import { useAuthStore } from '~/stores/auth'
import { useUserStore } from '~/stores/user'
import { useIsochroneStore } from '~/stores/isochrone'
import { useMiscStore } from '~/stores/misc'

// Debounce delay for batching message fetches (ms)
const BATCH_DELAY = 50

export const useMessageStore = defineStore({
  id: 'message',
  state: () => ({
    list: {},
    byUserList: {},

    // Count of unseen items
    count: 0,

    // In bounds
    bounds: {},
    activePostsCounter: 0,

    // The context from the last fetch, used for fetchMore (ModTools)
    context: null,
  }),
  actions: {
    init(config) {
      this.config = config
      // Messages we're in the process of fetching
      this.fetching = {}
      this.fetchingCount = 0
      this.fetchingMyGroups = null
      // Debounced batching state
      this.pendingFetches = []
      this.batchTimer = null
      // ModTools context
      this.context = null
    },
    async fetchCount(browseView, log = true) {
      const ret = await api(this.config).message.count(browseView, log)
      this.count = ret?.count || 0
      return this.count
    },
    async fetch(id, force) {
      id = parseInt(id)

      // Refetch after 10 minutes in case the state has changed.
      const now = Math.round(Date.now() / 1000)
      const expired = this.list[id]?.addedToCache
        ? now - this.list[id].addedToCache > 600
        : false

      // If already cached and not forcing/expired, return immediately
      if (!force && this.list[id] && !expired) {
        return this.list[id]
      }

      // If already fetching this ID, wait for the existing fetch
      if (this.fetching[id]) {
        try {
          await this.fetching[id]
          await nextTick()
        } catch (e) {
          this.handleFetchError(id, e)
        }
        return this.list[id]
      }

      // Add to pending batch and wait for the batch to complete
      return new Promise((resolve, reject) => {
        this.pendingFetches.push({ id, resolve, reject, force })

        // Clear existing timer and set a new one
        if (this.batchTimer) {
          clearTimeout(this.batchTimer)
        }

        this.batchTimer = setTimeout(() => {
          this.processMessageBatch()
        }, BATCH_DELAY)
      })
    },
    handleFetchError(id, e) {
      console.log('Failed to fetch message', e)
      if (e instanceof APIError && e.response.status === 404) {
        console.log('Message deleted, removing from store')
        delete this.list[id]

        const authStore = useAuthStore()
        const userUid = authStore.user?.id
        if (userUid && this.byUserList[userUid]) {
          this.byUserList[userUid] = this.byUserList[userUid].filter(
            (message) => message.id !== id
          )
        }
      } else {
        throw e
      }
    },
    async processMessageBatch() {
      if (!this.pendingFetches.length) {
        return
      }

      // Collect all pending fetches
      const pending = [...this.pendingFetches]
      this.pendingFetches = []
      this.batchTimer = null

      // Filter out IDs that are already cached (unless force/expired)
      const idsToFetch = []
      const forcedIds = new Set()
      const now = Math.round(Date.now() / 1000)

      for (const { id, force } of pending) {
        const expired = this.list[id]?.addedToCache
          ? now - this.list[id].addedToCache > 600
          : false

        if ((force || !this.list[id] || expired) && !this.fetching[id]) {
          if (!idsToFetch.includes(id)) {
            idsToFetch.push(id)
          }
          if (force) {
            forcedIds.add(id)
          }
        }
      }

      if (idsToFetch.length > 0) {
        try {
          // Pass force=true if any of the IDs were force-requested
          await this.fetchMultiple(idsToFetch, forcedIds.size > 0)
        } catch (e) {
          // Errors are handled per-message in fetchMultiple
          console.log('Batch fetch error', e)
        }
      }

      // Resolve all promises with the cached data
      for (const { id, resolve } of pending) {
        resolve(this.list[id])
      }
    },
    async fetchMultiple(ids, force) {
      const left = []

      ids.forEach((id) => {
        id = parseInt(id)

        if ((force || !this.list[id]) && !this.fetching[id]) {
          left.push(id)
        }
      })

      if (left.length) {
        // Split into chunks of 19 (API limit is < 20)
        const BATCH_SIZE = 19
        const chunks = []
        for (let i = 0; i < left.length; i += BATCH_SIZE) {
          chunks.push(left.slice(i, i + BATCH_SIZE))
        }

        // Process each chunk
        for (const chunk of chunks) {
          this.fetchingCount++

          // Create a shared promise for the batch fetch. Individual fetch() calls
          // will await this promise instead of making duplicate API requests.
          const batchPromise = api(this.config).message.fetch(
            chunk.join(','),
            false
          )

          // Set the fetching flag for each ID to the shared batch promise.
          chunk.forEach((id) => {
            this.fetching[id] = batchPromise
          })

          try {
            const msgs = await batchPromise

            if (msgs && msgs.forEach) {
              msgs.forEach((msg) => {
                this.list[msg.id] = msg
                if (this.list[msg.id]) {
                  this.list[msg.id].addedToCache = Math.round(Date.now() / 1000)
                }
              })
            } else if (typeof msgs === 'object') {
              this.list[msgs.id] = msgs
              if (this.list[msgs.id]) {
                this.list[msgs.id].addedToCache = Math.round(Date.now() / 1000)
              }
            } else {
              console.error('Failed to fetch', msgs)
            }
          } catch (e) {
            console.log('Failed to fetch messages', e)
            if (e instanceof APIError && e.response.status === 404) {
              console.log('Ignore 404 error')
            } else {
              throw e
            }
          } finally {
            this.fetchingCount--
            // Clear the fetching flags for all IDs in this batch.
            chunk.forEach((id) => {
              this.fetching[id] = null
            })
          }
        }
      }
    },
    async fetchInBounds(swlat, swlng, nelat, nelng, groupid, limit, cache) {
      let ret = []
      const key =
        swlat + ':' + swlng + ':' + nelat + ':' + nelng + ':' + groupid

      if (cache && this.bounds[key]) {
        ret = this.bounds[key]
      } else {
        // Don't cache this, as it might change.
        ret = await api(this.config).message.inbounds(
          swlat,
          swlng,
          nelat,
          nelng,
          groupid,
          limit
        )

        this.bounds[key] = ret
      }

      return ret
    },
    async search(params) {
      await this.clear()
      const ret = await api(this.config).message.search(params)
      return ret
    },
    async fetchMyGroups(gid) {
      let ret = null

      if (this.fetchingMyGroups) {
        ret = await this.fetchingMyGroups
        await nextTick()
      } else {
        this.fetchingMyGroups = api(this.config).message.mygroups(gid)
        ret = await this.fetchingMyGroups
        this.fetchingMyGroups = null
      }
      return ret
    },
    async fetchByUser(userid, active, force) {
      let messages = []

      const promise = api(this.config).message.fetchByUser(userid, active)

      const authStore = useAuthStore()
      const isOwnMessages = authStore.user?.id === userid

      // If we're getting non-active messages make sure we hit the server as the cache might be of active only.
      if (!active || force || !this.byUserList[userid]) {
        messages = await promise

        // Guard against null/undefined API response (e.g. in test mocks).
        if (!Array.isArray(messages)) {
          messages = []
        }

        if (isOwnMessages) {
          for (const message of messages) {
            message.unseen = false
          }
        }

        this.byUserList[userid] = messages
      } else if (this.byUserList[userid]) {
        // Fetch but don't wait
        promise.then((msgs) => {
          if (isOwnMessages) {
            for (const message of msgs) {
              message.unseen = false
            }
          }

          this.byUserList[userid] = msgs
        })

        messages = this.byUserList[userid]
      }

      return messages || []
    },
    async view(id) {
      await api(this.config).message.view(id)
    },
    async update(params) {
      const authStore = useAuthStore()
      const userUid = authStore.user?.id
      const data = await api(this.config).message.update(params)

      if (data.deleted) {
        // This can happen if we withdraw a post while it is pending.
        delete this.list[params.id]
        if (userUid && this.byUserList[userUid]) {
          this.byUserList[userUid] = this.byUserList[userUid].filter(
            (message) => message.id !== params.id
          )
        }
      } else {
        // Fetch back the updated version.
        const message = await this.fetch(params.id, true)
        this.list[params.id] = message
        if (userUid && this.byUserList[userUid]) {
          const index = this.byUserList[userUid].findIndex(
            (curMessage) => curMessage.id === params.id
          )
          if (index !== -1) {
            this.byUserList[userUid][index] = message

            // If this was an Outcome action, set hasoutcome flag so the post
            // moves from active to old posts list immediately
            if (params.action === 'Outcome') {
              this.byUserList[userUid][index].hasoutcome = true
            }
          }
        }
      }

      return data
    },
    async patch(params) {
      const data = await api(this.config).message.save(params)

      // Re-fetch the message to get the updated server state (e.g. after
      // deleting a photo).  Don't remove-then-readd — that destroys and
      // recreates any mounted component, losing local state such as
      // expanded/collapsed in summary view (#299).
      const miscStore = useMiscStore()
      if (miscStore.modtools) {
        const message = await this.fetchMT({
          id: params.id,
        })
        if (message) {
          this.list[message.id] = message
        }
      } else {
        await this.fetch(params.id, true) // Gets message.fromuser as int not object

        // Sync updated message into byUserList so that myposts reflects the
        // latest server state (e.g. hasoutcome cleared when a future deadline
        // is set — without this the post stays in "Old Posts" after extending).
        const updated = this.list[params.id]
        if (updated) {
          for (const userId in this.byUserList) {
            const idx = this.byUserList[userId].findIndex(
              (m) => m.id === params.id
            )
            if (idx !== -1) {
              this.byUserList[userId][idx] = {
                ...this.byUserList[userId][idx],
                ...updated,
                // Explicitly propagate hasoutcome from server state.
                // Without this, a cleared outcome (server omits hasoutcome with
                // omitempty) won't override the synthetic hasoutcome:true that
                // fetchByUser sets, so the post stays in "Old Posts".
                hasoutcome: updated.hasoutcome,
              }
              break
            }
          }
        }
      }

      return data
    },
    remove(item) {
      delete this.list[parseInt(item.id)]
    },
    clear() {
      this.$reset()
      this.clearContext()
    },
    clearContext() {
      // ModTools
      this.context = null
    },
    async promise(id, userid) {
      await api(this.config).message.update({
        id,
        userid,
        action: 'Promise',
      })

      await this.fetch(id, true)
    },
    async renege(id, userid) {
      await api(this.config).message.update({
        id,
        userid,
        action: 'Renege',
      })

      await this.fetch(id, true)
    },
    async addBy(id, userid, count) {
      await api(this.config).message.addBy(id, userid, count)
      await this.fetch(id, true)
    },
    async removeBy(id, userid) {
      await api(this.config).message.removeBy(id, userid)
      await this.fetch(id, true)
    },
    async intend(id, outcome) {
      await api(this.config).message.intend(id, outcome)
    },
    async fetchActivePostCount() {
      const authStore = useAuthStore()
      const userUid = authStore.user?.id

      if (!userUid) {
        this.activePostsCounter = 0
        return
      }

      const activeMessages = await api(this.config).message.fetchByUser(
        userUid,
        true
      )
      this.activePostsCounter = Array.isArray(activeMessages)
        ? activeMessages.length
        : 0
    },
    async markSeen(ids) {
      try {
        await api(this.config).message.markSeen(ids)
      } catch (e) {
        if (e?.response?.status === 401) {
          // Session expired while scrolling — not critical, silently ignore.
          return
        }

        throw e
      }

      const isochroneStore = useIsochroneStore()
      isochroneStore.markSeen(ids)

      // Also update local cache to prevent watcher loop in MessageList.vue
      const idSet = new Set(ids)
      Object.keys(this.list).forEach((key) => {
        const id = parseInt(key)
        if (idSet.has(id) && this.list[id]) {
          this.list[id].unseen = false
        }
      })

      await this.fetchCount()
    },
    // ModTools-specific methods below
    async searchMT(params) {
      if (params.searchmode === 'vector') {
        // Use V2 vector search endpoint directly
        const results = await api(this.config).message.search({
          search: params.term,
          messagetype: 'All',
          groupids: params.groupid ? String(params.groupid) : undefined,
          searchmode: 'vector',
        })

        if (!results || results.length === 0) return []

        // Fetch in parallel but preserve API score order via Promise.all index stability
        const fetched = await Promise.all(
          results.map(async (r) => {
            try {
              const message = await this.fetchMT({ id: r.id || r.msgid })
              if (message) {
                // Carry matchedon from search result onto the fetched message
                if (r.matchedon) {
                  message.matchedon = r.matchedon
                }
                this.list[message.id] = message
                return message.id
              }
            } catch (e) {
              console.log('Failed to fetch message', r.id, e?.message)
            }
            return null
          })
        )
        return fetched.filter((id) => id !== null)
      }

      // Existing keyword search path
      const data = await api(this.config).message.fetchMessages({
        subaction: 'searchall',
        search: params.term,
        exactonly: true,
        groupid: params.groupid,
      })
      if (!data.messages || data.messages.length === 0) return
      // Response is IDs only — fetch full details for each.
      await Promise.all(
        data.messages.map(async (id) => {
          try {
            const message = await this.fetchMT({ id })
            if (message) {
              this.list[message.id] = message
            }
          } catch (e) {
            console.log('Failed to fetch message', id, e?.message)
          }
        })
      )
    },
    async fetchMessagesMT(params) {
      if (params.context) {
        // Server expects context as a JSON-encoded string; URLSearchParams
        // would otherwise coerce an object to "[object Object]", the server
        // silently drops it, and infinite scroll caps at one page (~100).
        params.context = JSON.stringify(params.context)
      }
      if (!params.context) params.context = null

      const data = await api(this.config).message.fetchMessages(params)
      if (!data.messages || data.messages.length === 0) return
      const messageIDs = data.messages // Now returns IDs only (uint64 array)
      const context = data.context // Can be undefined if search complete

      if (params.collection !== 'Draft') {
        // We don't use context for drafts - there aren't many.
        this.context = context
      }

      // Fetch full message details in parallel for each ID.
      // Individual fetches may 404 if a message was deleted between listing and fetching.
      await Promise.all(
        messageIDs.map(async (id) => {
          try {
            const message = await this.fetchMT({
              id,
            })
            if (message) {
              if (!message.subject) message.subject = ''
              this.list[message.id] = message
            }
          } catch (e) {
            console.log('Failed to fetch message', id, e?.message)
          }
        })
      )

      return messageIDs
    },
    async fetchMT(params, logError = true) {
      const message = await api(this.config).message.fetchMT(params, logError)
      if (message && !message.subject) message.subject = ''
      return message
    },
    async updateMT(params) {
      // Rely on refresh elsewhere
      return await api(this.config).message.update(params)
    },
    async delete(params) {
      await api(this.config).message.delete(
        params.id,
        params.groupid,
        params.subject,
        params.stdmsgid,
        params.body
      )

      delete this.list[params.id]
    },
    async approveedits(params) {
      await api(this.config).message.approveEdits(params.id)

      this.remove({ id: params.id })
    },
    async revertedits(params) {
      await api(this.config).message.revertEdits(params.id)

      this.remove({ id: params.id })
    },
    async backToPending(id) {
      await api(this.config).message.update({
        id,
        action: 'BackToPending',
      })
      this.remove({ id })
    },
    async approve(id, groupid, subject, stdmsgid, body) {
      const msg = this.byId(id)
      const fromuser = msg?.fromuser

      await api(this.config).message.approve(
        id,
        groupid,
        subject,
        stdmsgid,
        body
      )
      this.remove({ id })

      // Re-fetch the sender so posting status changes from stdmsg take effect.
      if (fromuser) {
        const uid = typeof fromuser === 'number' ? fromuser : fromuser.id
        if (uid) {
          useUserStore().fetch(uid, true)
        }
      }
    },
    async reject(id, groupid, subject, stdmsgid, body) {
      const msg = this.byId(id)
      const fromuser = msg?.fromuser

      await api(this.config).message.reject(
        id,
        groupid,
        subject,
        stdmsgid,
        body
      )
      this.remove({ id })

      if (fromuser) {
        const uid = typeof fromuser === 'number' ? fromuser : fromuser.id
        if (uid) {
          useUserStore().fetch(uid, true)
        }
      }
    },
    async reply(params) {
      await api(this.config).message.reply(
        params.id,
        params.groupid,
        params.subject,
        params.stdmsgid,
        params.body
      )
      // Do not remove from list
    },
    async hold(params) {
      await api(this.config).message.hold(params.id)
      const message = await api(this.config).message.fetchMT({
        id: params.id,
      })
      this.list[message.id] = message
    },
    async release(params) {
      await api(this.config).message.release(params.id)
      const message = await api(this.config).message.fetchMT({
        id: params.id,
      })
      this.list[message.id] = message
    },
    async spam(params) {
      await api(this.config).message.spam(params.id, params.groupid)

      this.remove({ id: params.id })
    },
    async move(params) {
      await api(this.config).message.update({
        id: params.id,
        groupid: params.groupid,
        action: 'Move',
      })

      const message = await api(this.config).message.fetchMT({
        id: params.id,
      })
      this.list[message.id] = message
    },
    async searchMember(term, groupid) {
      const data = await api(this.config).message.fetchMessages({
        subaction: 'searchmemb',
        search: term,
        groupid,
      })
      await this.clear()
      if (!data.messages || data.messages.length === 0) return
      // Response is IDs only — fetch full details for each.
      await Promise.all(
        data.messages.map(async (id) => {
          try {
            const message = await this.fetchMT({ id })
            if (message) {
              this.list[message.id] = message
            }
          } catch (e) {
            console.log('Failed to fetch message', id, e?.message)
          }
        })
      )
    },
  },
  getters: {
    byId: (state) => {
      return (id) => state.list[id]
    },
    inBounds: (state) => (swlat, swlng, nelat, nelng, groupid) => {
      const key =
        swlat + ':' + swlng + ':' + nelat + ':' + nelng + ':' + groupid

      return key in this.bounds ? this.bounds[key] : []
    },
    all: (state) => Object.values(state.list),
    byUser: (state) => (userid) => {
      return state.byUserList[userid] || []
    },
    getByGroup: (state) => (groupid) => {
      // ModTools — match any group in the message's groups array (multi-group support).
      const gid = parseInt(groupid)
      const ret = Object.values(state.list).filter((message) => {
        return message.groups.some(
          (g) => parseInt(g.groupid) === gid
        )
      })
      return ret
    },
  },
})
